/**
 * Wildlife Strike Module
 * Logging, tracking, and analysis of wildlife strikes
 * Aligned with ICAO Doc 9137 and FAA AC 150/5200-32B
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import PhotoCapture from '../components/PhotoCapture';
import StrikeHotspotMap from '../components/StrikeHotspotMap';
import HotspotRecommendations from '../components/HotspotRecommendations';
import HelpTooltip from '../components/help/HelpTooltip';
import VoiceIncidentRecorder from '../components/VoiceIncidentRecorder';
import AIFieldSuggestions from '../components/AIFieldSuggestions';
import PolishButton from '../components/PolishButton';
import { Mic } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Bird, Plus, Search, BarChart3, AlertTriangle, CheckCircle, Clock,
  Plane, MapPin, Calendar, Eye, Target, TrendingUp, DollarSign,
  FileText, Play, Shield, ChevronRight, Info, CloudOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  draft: { color: 'bg-gray-500/20 text-gray-400', label: 'Draft' },
  submitted: { color: 'bg-blue-500/20 text-blue-400', label: 'Submitted' },
  under_review: { color: 'bg-amber-500/20 text-amber-400', label: 'Under Review' },
  verified: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Verified' },
  closed: { color: 'bg-slate-500/20 text-slate-400', label: 'Closed' }
};

// Damage level colors
const DAMAGE_COLORS = {
  none: 'bg-emerald-500/20 text-emerald-400',
  minor: 'bg-yellow-500/20 text-yellow-400',
  substantial: 'bg-orange-500/20 text-orange-400',
  destroyed: 'bg-red-500/20 text-red-400',
  unknown: 'bg-gray-500/20 text-gray-400'
};

// Size labels
const SIZE_LABELS = {
  small: 'Small (< 100g)',
  medium: 'Medium (100g - 1kg)',
  large: 'Large (1kg - 3kg)',
  very_large: 'Very Large (> 3kg)'
};

export default function WildlifeStrikePage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const { isOnline, isInitialized: offlineReady, saveWildlifeStrikeOffline } = useOffline();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [strikes, setStrikes] = useState([]);
  const [species, setSpecies] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [airports, setAirports] = useState([]);
  const [phases, setPhases] = useState([]);
  const [locations, setLocations] = useState([]);
  const [damageOptions, setDamageOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedStrike, setSelectedStrike] = useState(null);
  // Voice-to-Wildlife-Strike
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Free-text notes that seed the AI field-suggestions strip
  const [aiNotes, setAiNotes] = useState('');
  // Hotspot map tab
  const [hotspotDays, setHotspotDays] = useState(365);
  const [hotspotPoints, setHotspotPoints] = useState([]);
  const [hotspotsLoading, setHotspotsLoading] = useState(false);
  // DBSCAN-discovered clusters with mitigation recommendations
  const [clusters, setClusters] = useState([]);
  const [clusterMeta, setClusterMeta] = useState({ inputPoints: 0, epsM: 250, minSamples: 3 });
  const [clustersLoading, setClustersLoading] = useState(false);
  
  // Form state
  const [newStrike, setNewStrike] = useState({
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: '',
    aircraft_registration: '',
    aircraft_type: '',
    flight_number: '',
    airport_code: '',
    runway: '',
    phase_of_flight: 'unknown',
    wildlife_category: 'bird',
    species_name: '',
    number_struck: 1,
    size: 'medium',
    strike_locations: [],
    damage_level: 'unknown',
    effect_on_flight: 'none',
    photos: [],
    photo_geo: []
  });

  // Lazy-fetch hotspots only when the tab is opened or the window changes —
  // saves a 1-2s round-trip for users who never visit the map.
  useEffect(() => {
    if (activeTab !== 'hotspots') return;
    let cancelled = false;
    const run = async () => {
      setHotspotsLoading(true);
      setClustersLoading(true);
      try {
        // Use a wider eps_m for the demo dataset spanning multiple airports;
        // 250m is the production default that maps to one runway threshold,
        // but min_samples=2 surfaces 2-strike clusters which is useful while
        // the dataset is still small.
        const headers = { Authorization: `Bearer ${token}` };
        const [pointsRes, clustersRes] = await Promise.all([
          fetch(`${API_URL}/api/wildlife-strikes/analytics/hotspots?days=${hotspotDays}`, { headers }),
          fetch(`${API_URL}/api/wildlife-strikes/analytics/hotspot-clusters?days=${hotspotDays}&eps_m=2000&min_samples=2`, { headers }),
        ]);
        if (!pointsRes.ok) throw new Error(`hotspots HTTP ${pointsRes.status}`);
        if (!clustersRes.ok) throw new Error(`clusters HTTP ${clustersRes.status}`);
        const pData = await pointsRes.json();
        const cData = await clustersRes.json();
        if (!cancelled) {
          setHotspotPoints(pData.points || []);
          setClusters(cData.clusters || []);
          setClusterMeta({
            inputPoints: cData.input_points || 0,
            epsM: cData.eps_m || 250,
            minSamples: cData.min_samples || 3,
          });
        }
      } catch (e) {
        if (!cancelled) toast.error('Failed to load hotspot map');
      } finally {
        if (!cancelled) {
          setHotspotsLoading(false);
          setClustersLoading(false);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, hotspotDays, token]);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [strikesRes, statsRes, speciesRes, airportsRes, phasesRes, locationsRes, damageRes] = await Promise.all([
        fetch(`${API_URL}/api/wildlife-strikes/?limit=100`, { headers }),
        fetch(`${API_URL}/api/wildlife-strikes/stats/summary`, { headers }),
        fetch(`${API_URL}/api/wildlife-strikes/species`, { headers }),
        fetch(`${API_URL}/api/wildlife-strikes/reference/airports`, { headers }),
        fetch(`${API_URL}/api/wildlife-strikes/reference/phases`, { headers }),
        fetch(`${API_URL}/api/wildlife-strikes/reference/locations`, { headers }),
        fetch(`${API_URL}/api/wildlife-strikes/reference/damage-levels`, { headers })
      ]);
      
      if (strikesRes.ok) setStrikes(await strikesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (speciesRes.ok) setSpecies(await speciesRes.json());
      if (airportsRes.ok) setAirports(await airportsRes.json());
      if (phasesRes.ok) setPhases(await phasesRes.json());
      if (locationsRes.ok) setLocations(await locationsRes.json());
      if (damageRes.ok) setDamageOptions(await damageRes.json());
      
      // Fetch analytics
      const analyticsRes = await fetch(`${API_URL}/api/wildlife-strikes/analytics/summary`, { headers });
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSeedSpecies = async () => {
    try {
      const res = await fetch(`${API_URL}/api/wildlife-strikes/species/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Failed to seed species');
      }
    } catch (error) {
      toast.error('Error seeding species');
    }
  };

  const _resetStrikeForm = () => {
    setShowReportDialog(false);
    setNewStrike({
      incident_date: new Date().toISOString().split('T')[0],
      incident_time: '',
      aircraft_registration: '',
      aircraft_type: '',
      flight_number: '',
      airport_code: '',
      runway: '',
      phase_of_flight: 'unknown',
      wildlife_category: 'bird',
      species_name: '',
      number_struck: 1,
      size: 'medium',
      strike_locations: [],
      damage_level: 'unknown',
      effect_on_flight: 'none',
      photos: [],
      photo_geo: []
    });
  };

  const handleReportStrike = async () => {
    if (!newStrike.incident_date || !newStrike.aircraft_registration || !newStrike.airport_code) {
      toast.error('Please fill required fields: Date, Aircraft Registration, Airport');
      return;
    }

    // Offline path: persist to IndexedDB queue and let the auto-sync drain
    // it when connectivity returns. We don't refresh the list because the
    // server hasn't seen the strike yet.
    if (!isOnline) {
      if (!offlineReady) {
        toast.error('Offline storage not available. Please try again when online.');
        return;
      }
      try {
        await saveWildlifeStrikeOffline(newStrike);
        toast.success(
          <div className="flex items-center gap-2" data-testid="wildlife-offline-toast">
            <CloudOff className="h-4 w-4" />
            <span>Strike saved offline. Will sync when online.</span>
          </div>
        );
        _resetStrikeForm();
      } catch (err) {
        toast.error('Failed to save strike offline');
      }
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/wildlife-strikes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newStrike)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Wildlife strike reported: ${data.strike_number}`);
        _resetStrikeForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to report strike');
      }
    } catch (error) {
      // Network failure mid-submit (e.g. Wi‑Fi dropped after page load).
      // Treat as offline: queue and let auto-sync handle it.
      if (offlineReady) {
        try {
          await saveWildlifeStrikeOffline(newStrike);
          toast.success(
            <div className="flex items-center gap-2" data-testid="wildlife-offline-toast">
              <CloudOff className="h-4 w-4" />
              <span>Network error — strike queued offline.</span>
            </div>
          );
          _resetStrikeForm();
          return;
        } catch (_) { /* fall through */ }
      }
      toast.error('Error reporting strike');
    }
  };

  const handleVerifyStrike = async (strikeId) => {
    try {
      const res = await fetch(`${API_URL}/api/wildlife-strikes/${strikeId}/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Strike verified');
        fetchData();
        setShowDetailDialog(false);
      } else {
        toast.error('Failed to verify strike');
      }
    } catch (error) {
      toast.error('Error verifying strike');
    }
  };

  const filteredStrikes = strikes.filter(s =>
    s.strike_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.aircraft_registration?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.airport_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.species_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading wildlife strike data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('wildlife.title')}</h1>
          <p className="text-slate-400 mt-1">{t('wildlife.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => { setShowReportDialog(true); setVoiceOpen(true); }}
            variant="outline"
            className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
            data-testid="voice-file-strike-btn"
            title="Speak the strike — the AI will fill in the form for you"
          >
            <Mic className="w-4 h-4 mr-2" />
            File by Voice
          </Button>
          <Button onClick={() => setShowReportDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            {t('wildlife.newStrike')}
          </Button>
        </div>
      </div>

      <VoiceIncidentRecorder
        open={voiceOpen}
        mode="wildlife"
        onClose={() => setVoiceOpen(false)}
        onApply={(ex) => {
          if (!ex) return;
          // Map GPT-5.2 extraction → newStrike form state (leave existing values
          // untouched when the LLM couldn't determine a field).
          setNewStrike((prev) => ({
            ...prev,
            aircraft_registration: ex.aircraft_registration || prev.aircraft_registration,
            aircraft_type: ex.aircraft_type || prev.aircraft_type,
            flight_number: ex.flight_number || prev.flight_number,
            airport_code: ex.airport_icao || prev.airport_code,
            runway: ex.runway || prev.runway,
            phase_of_flight: ex.phase_of_flight || prev.phase_of_flight,
            wildlife_category: ex.wildlife_category || prev.wildlife_category,
            species_name: ex.species_name || prev.species_name,
            number_struck: ex.number_struck ?? prev.number_struck,
            size: ex.size || prev.size,
            damage_level: ex.damage_level || prev.damage_level,
            effect_on_flight: ex.effect_on_flight || prev.effect_on_flight,
            incident_date: ex.occurrence_date || prev.incident_date,
            incident_time: ex.occurrence_time || prev.incident_time,
          }));
          setVoiceOpen(false);
        }}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Bird className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_strikes}</p>
                  <p className="text-xs text-slate-400">Total Strikes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.strikes_this_year}</p>
                  <p className="text-xs text-slate-400">This Year</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.strikes_with_damage}</p>
                  <p className="text-xs text-slate-400">With Damage</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Target className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.damage_rate}%</p>
                  <p className="text-xs text-slate-400">Damage Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.verified_strikes}</p>
                  <p className="text-xs text-slate-400">Verified</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${(stats.total_repair_cost / 1000).toFixed(0)}K</p>
                  <p className="text-xs text-slate-400">Total Cost</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="strikes">Strike Reports</TabsTrigger>
          <TabsTrigger value="species">Species Database</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="hotspots" data-testid="wildlife-tab-hotspots">Hotspot Map</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Strikes */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bird className="w-5 h-5 text-red-400" />
                  Recent Strikes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {strikes.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Bird className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No strikes reported</p>
                    <p className="text-sm mt-1">Click &quot;Report Strike&quot; to log a new incident</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {strikes.slice(0, 5).map(strike => (
                      <div
                        key={strike.id}
                        className="p-3 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600 cursor-pointer"
                        onClick={() => {
                          setSelectedStrike(strike);
                          setShowDetailDialog(true);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{strike.strike_number}</span>
                              <Badge className={STATUS_CONFIG[strike.status]?.color || STATUS_CONFIG.draft.color}>
                                {STATUS_CONFIG[strike.status]?.label || 'Draft'}
                              </Badge>
                              <Badge className={DAMAGE_COLORS[strike.damage_level] || DAMAGE_COLORS.unknown}>
                                {strike.damage_level}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-400 mt-1">
                              {strike.airport_code} • {strike.aircraft_registration} • {strike.incident_date}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Analytics Summary */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Strike Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics ? (
                  <div className="space-y-4">
                    {/* By Category */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-2">By Wildlife Category</h4>
                      <div className="flex gap-2 flex-wrap">
                        {Object.entries(analytics.by_category || {}).map(([cat, count]) => (
                          <Badge key={cat} variant="outline" className="capitalize">
                            {cat}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* By Phase */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-2">High Risk Phases</h4>
                      <div className="flex gap-2 flex-wrap">
                        {(analytics.high_risk_phases || []).map(phase => (
                          <Badge key={phase} className="bg-red-500/20 text-red-400 capitalize">
                            {phase.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* Top Species */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-2">Top Species</h4>
                      {(analytics.top_species || []).slice(0, 5).map((s, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm py-1">
                          <span className="text-slate-300">{s.species}</span>
                          <span className="text-white font-medium">{s.count}</span>
                        </div>
                      ))}
                      {(!analytics.top_species || analytics.top_species.length === 0) && (
                        <p className="text-sm text-slate-400">No species data yet</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No analytics data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Strikes Tab */}
        <TabsContent value="strikes" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">All Strike Reports</CardTitle>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search strikes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64 bg-slate-900/50 border-slate-600"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {filteredStrikes.map(strike => (
                    <div
                      key={strike.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600 cursor-pointer"
                      onClick={() => {
                        setSelectedStrike(strike);
                        setShowDetailDialog(true);
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-red-500/20">
                          <Bird className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{strike.strike_number}</span>
                            <Badge className={STATUS_CONFIG[strike.status]?.color}>
                              {STATUS_CONFIG[strike.status]?.label}
                            </Badge>
                            <Badge className={DAMAGE_COLORS[strike.damage_level]}>
                              {strike.damage_level}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-400 mt-1">
                            <Plane className="w-3 h-3 inline mr-1" />
                            {strike.aircraft_registration} ({strike.aircraft_type || 'Unknown'})
                            <MapPin className="w-3 h-3 inline ml-3 mr-1" />
                            {strike.airport_code} {strike.runway ? `RWY ${strike.runway}` : ''}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {strike.incident_date} • {strike.species_name || 'Unidentified'} • {strike.phase_of_flight?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {filteredStrikes.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <Bird className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No strikes found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Species Tab */}
        <TabsContent value="species" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Species Database</CardTitle>
                <Button onClick={handleSeedSpecies} variant="outline" className="border-emerald-500/50 text-emerald-400">
                  <Play className="w-4 h-4 mr-2" />
                  Load Default Species
                </Button>
              </div>
              <CardDescription>Wildlife species commonly involved in strikes in the region</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {species.map(sp => (
                    <Card key={sp.id} className="bg-slate-900/50 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-white">{sp.common_name}</h4>
                            {sp.common_name_ar && (
                              <p className="text-sm text-slate-400" dir="rtl">{sp.common_name_ar}</p>
                            )}
                            <p className="text-xs text-slate-500 italic">{sp.scientific_name}</p>
                          </div>
                          <Badge className={
                            sp.risk_level === 'high' ? 'bg-red-500/20 text-red-400' :
                            sp.risk_level === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          }>
                            {sp.risk_level}
                          </Badge>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Badge variant="outline" className="text-xs capitalize">{sp.category}</Badge>
                          <Badge variant="outline" className="text-xs">{SIZE_LABELS[sp.size] || sp.size}</Badge>
                          {sp.average_weight_kg && (
                            <Badge variant="outline" className="text-xs">{sp.average_weight_kg} kg</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {species.length === 0 && (
                    <div className="col-span-3 text-center py-12 text-slate-400">
                      <Bird className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No species loaded</p>
                      <p className="text-sm mt-1">Click &quot;Load Default Species&quot; to populate the database</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* By Airport */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Strikes by Airport</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics && Object.keys(analytics.by_airport || {}).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.by_airport || {}).sort((a, b) => b[1] - a[1]).map(([airport, count]) => (
                      <div key={airport} className="flex items-center justify-between">
                        <span className="text-slate-300">{airport}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-red-500 rounded-full"
                              style={{ width: `${(count / Math.max(...Object.values(analytics.by_airport))) * 100}%` }}
                            />
                          </div>
                          <span className="text-white font-medium w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400">No airport data available</p>
                )}
              </CardContent>
            </Card>

            {/* By Phase */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Strikes by Flight Phase</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics && Object.keys(analytics.by_phase || {}).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(analytics.by_phase || {}).sort((a, b) => b[1] - a[1]).map(([phase, count]) => (
                      <div key={phase} className="flex items-center justify-between">
                        <span className="text-slate-300 capitalize">{phase.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${(count / Math.max(...Object.values(analytics.by_phase))) * 100}%` }}
                            />
                          </div>
                          <span className="text-white font-medium w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400">No phase data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Hotspot Map Tab — geo-tagged strike events on an interactive map */}
        <TabsContent value="hotspots" className="mt-4" data-testid="wildlife-hotspots-content">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-rose-400" />
                  Strike Hotspot Map
                </CardTitle>
                <p className="text-xs text-slate-400 mt-1">
                  Each marker is a geo-tagged strike (size + colour reflect damage).
                  Captured automatically from PhotoCapture GPS.
                </p>
              </div>
              <select
                value={hotspotDays}
                onChange={(e) => setHotspotDays(Number(e.target.value))}
                className="bg-slate-900 border border-slate-700 rounded-md text-sm text-slate-200 px-3 py-1.5"
                data-testid="hotspot-window-select"
              >
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 12 months</option>
                <option value={1825}>Last 5 years</option>
              </select>
            </CardHeader>
            <CardContent>
              {hotspotsLoading ? (
                <div className="h-[480px] flex items-center justify-center text-slate-400 text-sm">
                  Loading hotspots…
                </div>
              ) : (
                <StrikeHotspotMap points={hotspotPoints} height={520} />
              )}
            </CardContent>
          </Card>

          {/* DBSCAN-discovered clusters with mitigation recommendations */}
          <div className="mt-4">
            <HotspotRecommendations
              clusters={clusters}
              loading={clustersLoading}
              inputPoints={clusterMeta.inputPoints}
              epsM={clusterMeta.epsM}
              minSamples={clusterMeta.minSamples}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Report Strike Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white">Report Wildlife Strike</DialogTitle>
            <DialogDescription>Log a new wildlife strike incident</DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[70vh] mt-4 pr-4">
            <div className="space-y-6">
              {/* Incident Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-white border-b border-slate-700 pb-2">Incident Details</h4>
                {/* Optional free-text — feeds the AI field-suggestions strip below */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Notes (optional — helps the AI suggest fields)</Label>
                    <PolishButton
                      text={aiNotes}
                      kind="wildlife"
                      token={token}
                      onApply={(polished) => setAiNotes(polished)}
                    />
                  </div>
                  <Textarea
                    value={aiNotes}
                    onChange={(e) => setAiNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. Kestrel struck left engine on takeoff roll from OEJN 34L, no damage found on inspection…"
                    className="bg-slate-800 border-slate-600"
                    data-testid="wildlife-ai-notes"
                  />
                  <AIFieldSuggestions
                    kind="wildlife"
                    title=""
                    description={aiNotes}
                    token={token}
                    onApply={(patch) => setNewStrike((prev) => ({
                      ...prev,
                      ...(patch.species_name && { species_name: patch.species_name }),
                      ...(patch.wildlife_category && { wildlife_category: patch.wildlife_category }),
                      ...(patch.size && { size: patch.size }),
                      ...(patch.phase_of_flight && { phase_of_flight: patch.phase_of_flight }),
                      ...(patch.damage_level && { damage_level: patch.damage_level }),
                      ...(patch.effect_on_flight && { effect_on_flight: patch.effect_on_flight }),
                    }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={newStrike.incident_date}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, incident_date: e.target.value }))}
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Time (Local)</Label>
                    <Input
                      type="time"
                      value={newStrike.incident_time}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, incident_time: e.target.value }))}
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                </div>
              </div>

              {/* Aircraft Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-white border-b border-slate-700 pb-2">Aircraft Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="inline-flex items-center">
                      Aircraft Registration *
                      <HelpTooltip
                        label="Aircraft Registration"
                        body={"The tail number of the aircraft — the country prefix + up to 4 characters.\n\nSaudi Arabian aircraft start with **HZ-**:\n• HZ-AK001 (Saudia freighter)\n• HZ-ARD (Saudi flag carrier)\n\nOther examples: A6-EYT (Emirates), G-VBUS (Virgin), N123AA (US)."}
                        articleId="task-wildlife-strike"
                        side="left"
                        example="HZ-AK001"
                        onApplyExample={(v) => setNewStrike((prev) => ({ ...prev, aircraft_registration: v }))}
                      />
                    </Label>
                    <Input
                      value={newStrike.aircraft_registration}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, aircraft_registration: e.target.value.toUpperCase() }))}
                      placeholder="e.g., HZ-AK001"
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aircraft Type</Label>
                    <Input
                      value={newStrike.aircraft_type}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, aircraft_type: e.target.value }))}
                      placeholder="e.g., Boeing 777-300ER"
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="inline-flex items-center">
                      Flight Number
                      <HelpTooltip
                        label="Flight Number"
                        body={"Your operator's IATA/ICAO flight designator.\n\nFormat: 2-letter IATA airline code + up to 4 digits.\n• SV123 — Saudia\n• SVA789 — Saudia (ICAO 3-letter form)\n• EK47 — Emirates\n\nLeave blank if this was a positioning / ferry flight."}
                        articleId="task-wildlife-strike"
                        side="left"
                        example="SV123"
                        onApplyExample={(v) => setNewStrike((prev) => ({ ...prev, flight_number: v }))}
                      />
                    </Label>
                    <Input
                      value={newStrike.flight_number}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, flight_number: e.target.value.toUpperCase() }))}
                      placeholder="e.g., SV123"
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="inline-flex items-center">
                      Flight Phase
                      <HelpTooltip
                        label="Phase of Flight"
                        body={"Which stage of the flight the strike happened in.\n\nMost strikes occur during:\n• Takeoff / Climb — low altitude, birds flushed by engines\n• Approach / Landing — low over threshold\n\nPick 'unknown' if the pilot didn't specify."}
                        articleId="task-wildlife-strike"
                        side="left"
                      />
                    </Label>
                    <Select
                      value={newStrike.phase_of_flight}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, phase_of_flight: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {phases.map(p => (
                          <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h4 className="font-medium text-white border-b border-slate-700 pb-2">Location</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Airport Code (ICAO) *</Label>
                    <Select
                      value={newStrike.airport_code}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, airport_code: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue placeholder="Select airport..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {airports.map(a => (
                          <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Runway</Label>
                    <Input
                      value={newStrike.runway}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, runway: e.target.value.toUpperCase() }))}
                      placeholder="e.g., 33L"
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                </div>
              </div>

              {/* Wildlife Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-white border-b border-slate-700 pb-2">Wildlife Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={newStrike.wildlife_category}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, wildlife_category: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="bird">Bird</SelectItem>
                        <SelectItem value="bat">Bat</SelectItem>
                        <SelectItem value="mammal">Mammal</SelectItem>
                        <SelectItem value="reptile">Reptile</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="inline-flex items-center">
                      Species (if known)
                      <HelpTooltip
                        label="Species"
                        body={"The type of bird or animal that was struck.\n\nHow to identify:\n• Check any remains (feathers, size, colour)\n• Ask the pilot / ground crew what they saw\n• If unsure, pick 'Unknown / Not Identified' — do NOT guess\n\nAccurate species data feeds the airport's Annex 14 §9.4 wildlife-hazard programme."}
                        articleId="task-wildlife-strike"
                        side="left"
                        example="Kestrel"
                        onApplyExample={(v) => setNewStrike((prev) => ({ ...prev, species_name: v }))}
                      />
                    </Label>
                    <Select
                      value={newStrike.species_name || 'unknown'}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, species_name: v === 'unknown' ? '' : v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue placeholder="Select species..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="unknown">Unknown / Not Identified</SelectItem>
                        {species.filter(s => s.category === newStrike.wildlife_category).map(sp => (
                          <SelectItem key={sp.id} value={sp.common_name}>{sp.common_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Size</Label>
                    <Select
                      value={newStrike.size}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, size: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="small">Small (&lt; 100g)</SelectItem>
                        <SelectItem value="medium">Medium (100g - 1kg)</SelectItem>
                        <SelectItem value="large">Large (1kg - 3kg)</SelectItem>
                        <SelectItem value="very_large">Very Large (&gt; 3kg)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Number Struck</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newStrike.number_struck}
                      onChange={(e) => setNewStrike(prev => ({ ...prev, number_struck: parseInt(e.target.value) || 1 }))}
                      className="bg-slate-800 border-slate-600"
                    />
                  </div>
                </div>
              </div>

              {/* Damage */}
              <div className="space-y-4">
                <h4 className="font-medium text-white border-b border-slate-700 pb-2">Damage Assessment</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="inline-flex items-center">
                      Damage Level
                      <HelpTooltip
                        label="Damage Level"
                        body={"How badly the aircraft was damaged, ICAO Doc 9137 scale:\n\n• None — no visible damage\n• Minor — small dents, blood, feathers; no repair needed before next flight\n• Substantial — repair required; aircraft grounded\n• Destroyed — write-off / hull loss\n\nWhen in doubt, upgrade to 'Substantial' and let maintenance verify."}
                        articleId="task-wildlife-strike"
                        side="left"
                        example="minor"
                        onApplyExample={(v) => setNewStrike((prev) => ({ ...prev, damage_level: v }))}
                      />
                    </Label>
                    <Select
                      value={newStrike.damage_level}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, damage_level: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {damageOptions.map(d => (
                          <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Effect on Flight</Label>
                    <Select
                      value={newStrike.effect_on_flight}
                      onValueChange={(v) => setNewStrike(prev => ({ ...prev, effect_on_flight: v }))}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="aborted_takeoff">Aborted Takeoff</SelectItem>
                        <SelectItem value="precautionary_landing">Precautionary Landing</SelectItem>
                        <SelectItem value="engine_shutdown">Engine Shutdown</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Strike Locations */}
                <div className="space-y-2">
                  <Label>Strike Location(s)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {locations.map(loc => (
                      <div key={loc.key} className="flex items-center gap-2">
                        <Checkbox
                          checked={newStrike.strike_locations.includes(loc.key)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewStrike(prev => ({
                                ...prev,
                                strike_locations: [...prev.strike_locations, loc.key]
                              }));
                            } else {
                              setNewStrike(prev => ({
                                ...prev,
                                strike_locations: prev.strike_locations.filter(l => l !== loc.key)
                              }));
                            }
                          }}
                        />
                        <Label className="text-sm text-slate-300">{loc.name}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Photo evidence — works offline (queued in IndexedDB) */}
                <div className="space-y-2" data-testid="wildlife-photos-section">
                  <Label>Photo Evidence (optional, max 5)</Label>
                  <p className="text-xs text-slate-500">
                    Photos are compressed locally, GPS-tagged at capture time, and saved with the report — they sync automatically when reconnected.
                  </p>
                  <PhotoCapture
                    value={newStrike.photos}
                    onChange={(photos) => setNewStrike(prev => ({ ...prev, photos }))}
                    geoTag={true}
                    geoValue={newStrike.photo_geo || []}
                    onGeoChange={(photo_geo) => setNewStrike(prev => ({ ...prev, photo_geo }))}
                    testId="wildlife-photos"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
                <Button onClick={handleReportStrike} className="bg-blue-600 hover:bg-blue-700">
                  Submit Report
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Strike Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedStrike?.strike_number}
              {selectedStrike && (
                <>
                  <Badge className={STATUS_CONFIG[selectedStrike.status]?.color}>
                    {STATUS_CONFIG[selectedStrike.status]?.label}
                  </Badge>
                  <Badge className={DAMAGE_COLORS[selectedStrike.damage_level]}>
                    {selectedStrike.damage_level}
                  </Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedStrike && (
            <ScrollArea className="h-[60vh] mt-4 pr-4">
              <div className="space-y-4">
                {/* Incident Info */}
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h4 className="font-medium text-white mb-3">Incident Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Date:</span>
                      <span className="text-white ml-2">{selectedStrike.incident_date}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Time:</span>
                      <span className="text-white ml-2">{selectedStrike.incident_time || 'Not recorded'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Airport:</span>
                      <span className="text-white ml-2">{selectedStrike.airport_code}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Runway:</span>
                      <span className="text-white ml-2">{selectedStrike.runway || 'Not recorded'}</span>
                    </div>
                  </div>
                </div>

                {/* Aircraft Info */}
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h4 className="font-medium text-white mb-3">Aircraft</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Registration:</span>
                      <span className="text-white ml-2">{selectedStrike.aircraft_registration}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Type:</span>
                      <span className="text-white ml-2">{selectedStrike.aircraft_type || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Flight:</span>
                      <span className="text-white ml-2">{selectedStrike.flight_number || 'Not recorded'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Phase:</span>
                      <span className="text-white ml-2 capitalize">{selectedStrike.phase_of_flight?.replace('_', ' ')}</span>
                    </div>
                  </div>
                </div>

                {/* Wildlife Info */}
                <div className="p-4 rounded-lg bg-slate-800/50">
                  <h4 className="font-medium text-white mb-3">Wildlife</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Category:</span>
                      <span className="text-white ml-2 capitalize">{selectedStrike.wildlife_category}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Species:</span>
                      <span className="text-white ml-2">{selectedStrike.species_name || 'Unidentified'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Size:</span>
                      <span className="text-white ml-2">{SIZE_LABELS[selectedStrike.size] || selectedStrike.size}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Number:</span>
                      <span className="text-white ml-2">{selectedStrike.number_struck}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {selectedStrike.status === 'submitted' && (
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      onClick={() => handleVerifyStrike(selectedStrike.id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verify Strike
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
